import { getApiEndpoint } from "../config/config";

// AI pay extraction: read a compensation document with AI and get back a DRAFT
// pay structure (commission scale, upfront pay, bonuses, expenses) to prefill
// the pay editor with. Nothing here creates or changes a deal — a human reviews
// the draft and submits it through the normal pay endpoints. Mirrors the iOS
// RepDocAIUploadFlow against the same API.

// Extraction is synchronous on the server and can take a minute on a long PDF,
// so these calls get a generous timeout of their own rather than the default.
const EXTRACTION_TIMEOUT_MS = 240000;

const extract = async (path, jwt) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXTRACTION_TIMEOUT_MS);
  try {
    const response = await fetch(getApiEndpoint(path), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || `HTTP ${response.status}`);
    }
    return payload.data;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error("The document took too long to read. Please try again.");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

export const payExtractionService = {
  // Read pay off a file uploaded to a rep's profile (Documents tab).
  extractRepAttachmentPay: (repId, attachmentId, jwt) =>
    extract(
      `/v1/rep/${repId}/documents/attachments/${attachmentId}/extract-pay`,
      jwt
    ),

  // Read pay off an e-signature document.
  extractDocumentPay: (documentId, jwt) =>
    extract(`/v1/esignature/documents/${documentId}/extract-pay`, jwt),

  // Point a saved pay section at the document its details came from, so the
  // rep's pay view can show where the numbers came from. Upserts the section's
  // single live row. Best-effort — a failed tag never blocks a saved deal.
  async tagPaySectionSource({ repId, section, documentType, documentId }, jwt) {
    try {
      await fetch(getApiEndpoint("/v1/ps/pay-section-sources"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          rep_id: repId,
          section,
          document_type: documentType,
          document_id: String(documentId),
        }),
      });
    } catch (err) {
      // Ignored on purpose.
    }
  },
};

// ── Draft → editor state ───────────────────────────────────────────────────
// The editor works in the same shapes the deal endpoints accept, plus local-only
// `_`-prefixed keys (a list key, and the verbatim document text a value was read
// from). `payDraftPayload` strips those before anything is sent.

let itemSeq = 0;
export const nextEditorItemId = () => `ai-${++itemSeq}`;

const num = value => (value == null || value === "" ? null : Number(value));

const draftUpfront = draft => {
  const up = draft?.upfront_pay;
  if (!up) return null;
  return {
    upfront_pay_type: up.upfront_pay_type ?? null,
    fixed_amount: num(up.fixed_amount),
    fixed_percentage: num(up.fixed_percentage),
    scale_lines: (up.scale_lines || []).map(l => ({
      accounts_amount: num(l.accounts_amount),
      amount: num(l.amount),
      percentage: num(l.percentage),
    })),
    _sourceText: up.source_text || null,
  };
};

const draftBonuses = draft =>
  (draft?.bonuses || []).map(b => ({
    _id: nextEditorItemId(),
    _sourceText: b.source_text || null,
    name: b.name,
    bonus_type_category: b.bonus_type_category,
    bonus_type_earned_by_item: b.bonus_type_earned_by_item,
    bonus_type_earned_by_type: b.bonus_type_earned_by_type,
    item_name: b.item_name ?? null,
    item_monetary_value: b.item_monetary_value ?? null,
    bonus_amount: num(b.bonus_amount),
    bonus_revenue_amount: num(b.bonus_revenue_amount),
    bonus_accounts_amount: num(b.bonus_accounts_amount),
    bonus_recruits_amount: num(b.bonus_recruits_amount),
    recurring_initial_amount: num(b.recurring_initial_amount),
    recurring_repeated_amount: num(b.recurring_repeated_amount),
    recurring_schedule_type: b.recurring_schedule_type ?? null,
    // Per-account ("ACCOUNTS") bonuses may only count accounts whose agreement
    // term is at least this many months. null/absent = counts every account.
    min_agreement_months: num(b.min_agreement_months),
  }));

// Penalty terms the extractor read off the agreement — amounts the rep owes only
// if they do something specific (breaching a covenant, bringing a pet into
// housing, leaving early). These are POTENTIAL: carrying one on a deal costs the
// rep nothing. Money moves only when a person records an application, which is a
// separate endpoint and never happens here.
const draftPenalties = draft =>
  (draft?.penalties || []).map(p => ({
    _id: nextEditorItemId(),
    label: p.label,
    trigger_description: p.trigger_description ?? null,
    penalty_basis: p.penalty_basis || "FIXED",
    amount: num(p.amount),
    source_text: p.source_text ?? null,
  }));

// Production-bonus Quality Deductions. The AI extractor does not read these off a
// document, so this is normally empty — an existing deal's `reductions` are
// seeded onto the editor state separately (see RepDocAIExtractModal.seedState).
const draftReductions = draft =>
  (draft?.reductions || []).map(r => ({
    _id: nextEditorItemId(),
    reduction_type: r.reduction_type,
    label: r.label ?? null,
    threshold_months: num(r.threshold_months),
    per_account_amount: num(r.per_account_amount),
    first_tier_cap_pct: num(r.first_tier_cap_pct),
    manual_count: num(r.manual_count),
    benchmark_tiers: (r.benchmark_tiers || []).map(t => ({
      contract_value_threshold: Number(t.contract_value_threshold) || 0,
      benchmark_percentage: Number(t.benchmark_percentage) || 0,
    })),
  }));

const draftExpenses = draft =>
  (draft?.expenses || []).map(e => ({
    _id: nextEditorItemId(),
    _sourceText: e.source_text || null,
    name: e.name,
    expense_type_category: e.expense_type_category,
    backend_percentage: num(e.backend_percentage),
    backend_fixed_amount: num(e.backend_fixed_amount),
    recurring_frontend_amount: num(e.recurring_frontend_amount),
    recurring_schedule_type: e.recurring_schedule_type ?? null,
  }));

// The scale table always shows a locked $0 "Start" tier, so an empty scale has
// to carry one too — otherwise what is on screen and what gets sent disagree.
export const withBaseTier = lines =>
  lines && lines.length > 0 ? lines : [{ revenue_amount: 0, percentage: 0 }];

// One AI draft → the editor's working state.
export const draftToEditorState = draft => ({
  commissionLines: (draft?.commission_scale_lines || []).map(l => ({
    revenue_amount: Number(l.revenue_amount) || 0,
    percentage: Number(l.percentage) || 0,
  })),
  commissionSourceText: draft?.commission_source_text || null,
  upfront: draftUpfront(draft),
  bonuses: draftBonuses(draft),
  expenses: draftExpenses(draft),
  reductions: draftReductions(draft),
  penalties: draftPenalties(draft),
});

// ── Editor state → API payload ─────────────────────────────────────────────

// Drop every local-only key. The API validates with a global whitelist, so an
// unknown field is a 400, not a silently ignored extra.
const stripLocal = item => {
  const out = {};
  Object.keys(item || {}).forEach(key => {
    if (!key.startsWith("_")) out[key] = item[key];
  });
  return out;
};

// The review-shaped pay body every save path takes: PUT /v1/ps/user-deal/:id,
// POST /v1/ps/user-deal/for-rep/:repId, and the bulk-import confirm.
export const payDraftPayload = (
  state,
  { dealType = "REP", percentage = 100 } = {}
) => ({
  deal_type: dealType,
  users: [
    {
      percentage,
      commission_scale_lines: (state.commissionLines || [])
        .slice()
        .sort((a, b) => a.revenue_amount - b.revenue_amount)
        .map((line, i) => ({
          revenue_amount: i === 0 ? 0 : Number(line.revenue_amount) || 0,
          percentage: Number(line.percentage) || 0,
        })),
      ...(state.upfront ? { upfront_pay: stripLocal(state.upfront) } : {}),
      bonuses: (state.bonuses || []).map(stripLocal),
    },
  ],
  expenses: (state.expenses || []).map(stripLocal),
  // Deal-level production-bonus Quality Deductions, sent alongside expenses.
  reductions: (state.reductions || []).map(stripLocal),
  // Penalty terms. Sent with the deal because they are contract terms; applying
  // one is a separate, deliberate act through the penalty endpoints.
  //
  // OMITTED WHEN EMPTY on purpose. The API validates with
  // forbidNonWhitelisted, so an API that predates the `penalties` field rejects
  // the whole save with a 400 rather than ignoring it. Sending the key only when
  // there is something to send means this build works against both the old and
  // the new API, and the two deploys do not have to be ordered.
  ...((state.penalties || []).length
    ? { penalties: state.penalties.map(stripLocal) }
    : {}),
  // The MD / override scales' active windows ride on `_`-prefixed state keys
  // (local-only by convention, so stripLocal never leaks them raw) and map to
  // their real payload keys here. Only sent when the editor seeded them —
  // `{start_date, end_date}` or null (null = full season). The caller is
  // responsible for sending the matching md/override scale lines.
  ...(state._mdPeriod !== undefined
    ? { md_scale_period: state._mdPeriod }
    : {}),
  ...(state._overridePeriod !== undefined
    ? { override_scale_period: state._overridePeriod }
    : {}),
});

// Which pay sections this document actually filled in — the ones the AI
// suggested that survived review. Used to tag pay-section sources after a save.
export const filledSections = (draft, state) => {
  const out = [];
  if (
    (draft?.commission_scale_lines || []).length > 0 &&
    (state.commissionLines || []).length > 0
  ) {
    out.push("COMMISSION");
  }
  if (draft?.upfront_pay && state.upfront) out.push("UPFRONT");
  if ((draft?.bonuses || []).length > 0 && (state.bonuses || []).length > 0) {
    out.push("BONUSES");
  }
  if ((draft?.expenses || []).length > 0 && (state.expenses || []).length > 0) {
    out.push("EXPENSES");
  }
  return out;
};
